import {
    AddIcon,
    Button,
    Checkbox,
    Flex,
    FlexItem,
    Input,
    InputProps,
    SearchIcon,
    Text,
    TrashCanIcon
} from "@fluentui/react-northstar";
import * as React from "react";
import {useCallback, useMemo, useState} from "react";
import languageObject from "translations";
import "./emailsGrid.scss";
import {AddEmailDialog} from "./AddEmailDialog";

type Props = {
    emails: Array<string> | undefined,
    onChange: (emails: Array<string>) => void,
    disabled: boolean
}

type State = {
    searchQuery: string,
    selectedEmails: Array<string>,
    showAddEmailDialog: boolean,
}

const initialState: State = {
    searchQuery: "",
    selectedEmails: [],
    showAddEmailDialog: false,
}

export const EmailsGrid = (props: Props) => {
    const [state, setState] = useState<State>({...initialState});
    const {searchQuery} = state;

    const handleChangeSearchQuery = useCallback((_: React.SyntheticEvent, data: InputProps | undefined) => {
        const value = (data?.value ?? "") + "";
        setState((prevState): State => ({...prevState, searchQuery: value.toLowerCase()}));
    }, []);

    const handleToggleSelectEmail = useCallback((email: string) => () => {
        if (props.disabled) return;
        setState((prevState): State => {
            const selectedEmails = [...prevState.selectedEmails];
            const index = selectedEmails.indexOf(email);
            if (index > -1) selectedEmails.splice(index, 1);
            else selectedEmails.push(email);
            return {...prevState, selectedEmails};
        });
    }, [props.disabled]);

    const handleDeleteSelectedEmails = () => {
        const newEmails = (props.emails ?? []).filter(e => !state.selectedEmails.includes(e));
        props.onChange(newEmails);
        setState((prevState): State => ({...prevState, selectedEmails: []}));
    }

    const handleAddEmail = (email: string) => {
        const newEmails = (props.emails ?? []);
        newEmails.push(email);
        props.onChange(newEmails);
        setState((prevState): State => ({...prevState, showAddEmailDialog: false}));
    }

    const handleShowAddEmailDialog = useCallback(() => {
        setState((prevState): State => ({...prevState, showAddEmailDialog: true}));
    }, []);

    const handleCloseAddEmailDialog = useCallback(() => {
        setState((prevState): State => ({...prevState, showAddEmailDialog: false}));
    }, []);

    const filteredEmails = useMemo(() => {
        if (!searchQuery) return props.emails;
        return props.emails?.filter(e => e.toLowerCase().includes(searchQuery));
    }, [state.searchQuery, props.emails]);

    if (!props.emails) return null;
    return (
        <Flex fill column className={"overflow-hidden"} gap={"gap.medium"}>
            <Flex vAlign={"center"} gap={"gap.small"}>
                <Flex styles={{width: "300px"}}>
                    <Input
                        fluid
                        value={searchQuery}
                        icon={<SearchIcon outline/>}
                        iconPosition={"start"}
                        placeholder={languageObject.translate("SearchUpn")}
                        inverted
                        onChange={handleChangeSearchQuery}
                        disabled={props.disabled}
                    />
                </Flex>
                <Text
                    content={props.emails.length + " " + languageObject.translate(props.emails.length < 2 ? "Email" : "Emails").toLowerCase()}/>
                <FlexItem push>
                    <Flex gap={"gap.small"}>
                        {state.selectedEmails.length > 0 && (
                            <Button
                                className={props.disabled ? "" : "delete_button"}
                                primary
                                icon={<TrashCanIcon outline/>}
                                content={languageObject.translate(state.selectedEmails.length === 1 ? "DeleteSelectedEmail" : "DeleteCountEmails", {count: state.selectedEmails.length})}
                                onClick={handleDeleteSelectedEmails}
                                disabled={props.disabled}
                            />
                        )}
                        <Button
                            icon={<AddIcon outline/>}
                            content={languageObject.translate("AddUpn")}
                            onClick={handleShowAddEmailDialog}
                            disabled={props.disabled}
                        />
                    </Flex>
                </FlexItem>
            </Flex>
            <Flex fill className={"overflow-scroll"}>
                <div className={"emails-grid"}>
                    {filteredEmails?.map((email) => (
                        <Flex className={"emails-grid-item"} key={email} vAlign={"center"}
                              onClick={handleToggleSelectEmail(email)}>
                            <Checkbox disabled={props.disabled} checked={state.selectedEmails.includes(email)}/>
                            <Text truncated title={email} content={email}/>
                        </Flex>
                    ))}
                </div>
            </Flex>
            <AddEmailDialog
                show={state.showAddEmailDialog}
                onClose={handleCloseAddEmailDialog}
                onConfirm={handleAddEmail}
            />
        </Flex>
    )
}